import { Check, Loader2, X } from 'lucide-react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Nút tròn kính của sheet iOS 26 — ✕ đóng, ✓ áp dụng (người dùng chốt
 * 2026-10-05). Chất liệu nằm ở `.ios-glass-btn` trong `index.css`.
 *
 * Chỉ có biểu tượng, nên `label` là **bắt buộc** — nó thành `aria-label` lẫn
 * `title`: không có chữ trên nút thì trình đọc màn hình và con trỏ chuột là hai
 * chỗ duy nhất nói nút làm gì.
 */
export const GlassIconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
    readonly variant: 'close' | 'apply';
    readonly label: string;
    /** `sm` 28px cho góc hộp thoại, `md` 36px cho thanh đầu drawer/sheet. */
    readonly size?: 'sm' | 'md';
    /** Vòng quay thay biểu tượng — đang lưu. Không tự khoá nút: chỗ gọi quyết. */
    readonly pending?: boolean;
  }
>(function GlassIconButton(
  { variant, label, size = 'sm', pending = false, className, type = 'button', ...rest },
  ref,
) {
  const Icon = pending ? Loader2 : variant === 'apply' ? Check : X;
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      data-variant={variant}
      className={cn('ios-glass-btn', size === 'md' ? 'size-9' : 'size-7', className)}
      aria-busy={pending || undefined}
      {...rest}
    >
      <Icon
        aria-hidden
        strokeWidth={2.5}
        className={cn(size === 'md' ? 'size-[18px]' : 'size-4', pending && 'animate-spin')}
      />
    </button>
  );
});
